import React, { Suspense, lazy } from 'react';
import { Button } from '@/components/ui/button';
import { Navigation } from '@/components/Navigation';
import { LimitedSpots } from '@/components/LimitedSpots';
import { LogoStrip } from '@/components/LogoStrip';
import { SimpleReveal } from '@/components/SimpleReveal';
import { ProductPreviewCarousel } from '@/components/ProductPreviewCarousel';
import { ScrollProgress } from '@/components/ScrollProgress';
import { SequentialText } from '@/components/SequentialText';
import { CountingMetric } from '@/components/CountingMetric';
import { ParticleBackground } from '@/components/ParticleBackground';
import { AnimatedWord } from '@/components/AnimatedWord';
import { IntroducingBar } from '@/components/IntroducingBar';
import { WelcomeVideoSection, RealIssueSection } from '@/components/WelcomeSection';
import { DarkChapter } from '@/components/layout/DarkChapter';
import { ChevronDown } from 'lucide-react';
import { ArtMeetsScienceFormula } from '@/components/ArtMeetsScienceFormula';
import { FounderMessageSection } from '@/components/FounderMessageSection';
import { VideoPhoneMockup } from '@/components/VideoPhoneMockup';

// Helper to retry failed chunk loads
const lazyWithRetry = <T extends React.ComponentType<any>>(
  importer: () => Promise<{ default: T }>
) =>
  lazy(async () => {
    try {
      return await importer();
    } catch (err) {
      const message = String(err);
      const looksLikeChunkError = /ChunkLoadError|Loading chunk|Importing a module script failed|Failed to fetch dynamically imported module/.test(message);
      if (looksLikeChunkError) {
        const retries = Number(sessionStorage.getItem('lazy-retry') || '0');
        if (retries < 1) {
          sessionStorage.setItem('lazy-retry', String(retries + 1));
          const url = new URL(window.location.href);
          url.searchParams.set('v', String(Date.now()));
          window.location.replace(url.toString());
        }
      }
      throw err;
    }
  });

// Lazy load below-fold components for better performance
const DeliverablesTimeline = lazyWithRetry(() => import('@/components/DeliverablesTimeline'));
const FAQSection = lazyWithRetry(() => import('@/components/FAQSection'));
const FounderNote = lazyWithRetry(() => import('@/components/FounderNote'));
const WhoThisIsFor = lazyWithRetry(() => import('@/components/WhoThisIsFor'));
const DetailedCaseStudyTabs = lazyWithRetry(() => import('@/components/DetailedCaseStudyTabs'));
const TestimonialsSection = lazyWithRetry(() => import('@/components/TestimonialsSection'));
const BeforeAfterCTA = lazyWithRetry(() => import('@/components/BeforeAfterCTA'));
const Footer = lazyWithRetry(() => import('@/components/Footer'));

export default function ArtistEngine() {
  const applyUrl = "https://a23j5fry30t.typeform.com/to/CzjaFT5z";

  const whyNowPhrases = [
    "Algorithms reward consistent signals",
    "Artists with systems grow faster than those without",
    "Your music deserves a structure that compounds"
  ];

  return (
    <>
      <ScrollProgress />
      <div className="min-h-screen text-text">
        <Navigation />
          
          {/* Unified Dark Chapter: Hero + Video Section */}
          <DarkChapter>
            {/* Hero Section */}
            <section id="hero" data-nav-theme="dark" className="relative min-h-0 lg:min-h-[90vh] flex items-start overflow-visible pt-20 lg:pt-32 pb-2 lg:pb-12">
              {/* Main Content - Two Column Grid */}
              <div className="w-full max-w-7xl mx-auto px-8 lg:px-12">
                <div className="relative lg:flex lg:items-start">
                  
                  {/* Left Column - Text Content */}
                  <div className="max-w-4xl lg:max-w-2xl mt-4 lg:mt-16 lg:pr-12">
                    {/* Agency Badge */}
            <SimpleReveal delay={60}>
              <div className="mb-4 text-center lg:text-left">
                <div className="inline-flex items-center bg-white/5 border border-white/10 rounded-md px-2 lg:px-2.5 py-1 lg:py-1.5 gap-2">
                  <span className="font-mono text-[10px] lg:text-xs text-white/50 tracking-[0.15em] uppercase">
                    &gt; THE AGENCY
                  </span>
                </div>
              </div>
            </SimpleReveal>

                    <SimpleReveal delay={120}>
                      {/* Mobile Headline - 3 lines, centered */}
                      <h1 className="block md:hidden font-headline font-bold text-text text-center mb-4 text-[clamp(2.5rem,5.5vw,4rem)] leading-[1.05] tracking-[-0.03em]">
                        Make Your<br />
                        <span className="whitespace-nowrap">Music Impossible</span><br />
                        To <AnimatedWord words={['Ignore', 'Deny', 'Forget']} className="bg-gradient-to-r from-[hsl(210,100%,55%)] via-[hsl(195,100%,50%)] to-[hsl(260,80%,60%)] bg-clip-text text-transparent" />
                      </h1>

                      {/* iPad Headline - 2 lines, centered */}
                      <h1 className="hidden md:block lg:hidden font-headline font-bold text-text text-center mb-4 text-[clamp(2.5rem,5.5vw,4rem)] leading-[1.05] tracking-[-0.03em]">
                        Make Your Music<br />
                        Impossible To <AnimatedWord words={['Ignore', 'Deny', 'Forget']} className="bg-gradient-to-r from-[hsl(210,100%,55%)] via-[hsl(195,100%,50%)] to-[hsl(260,80%,60%)] bg-clip-text text-transparent" />
                      </h1>

                      {/* Desktop Headline - 2 lines, left-aligned */}
                      <h1 className="hidden lg:block font-headline font-bold text-text text-left mb-4 text-[clamp(2.5rem,5.5vw,4rem)] leading-[1.05] tracking-[-0.03em] max-w-[920px]">
                        Make Your Music<br />
                        <span className="sm:whitespace-nowrap">Impossible To <AnimatedWord words={['Ignore', 'Deny', 'Forget']} className="bg-gradient-to-r from-[hsl(210,100%,55%)] via-[hsl(195,100%,50%)] to-[hsl(260,80%,60%)] bg-clip-text text-transparent" /></span>
                      </h1>
                    </SimpleReveal>
                    
                    <SimpleReveal delay={240}>
                      <p className="text-sm lg:text-xl text-white/80 text-center md:text-center lg:text-left max-w-xl md:mx-auto lg:mx-0 mb-4 lg:mb-8 leading-[1.5]">
                        We turn talented artists into unstoppable forces. Proven systems. Expert execution. Momentum that compounds.
                      </p>
                    </SimpleReveal>
                    
                    <SimpleReveal delay={360}>
                      <div className="hidden lg:flex flex-col sm:flex-row gap-3 items-start">
                        {/* White button with blue gradient ring */}
                        <div className="inline-block group relative shadow-[0_0_20px_rgba(0,113,227,0.25)] rounded-xl">
                          <div 
                            className="absolute -inset-[3px] rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                            style={{
                              background: 'linear-gradient(90deg, #0071e3, #00bfff, #0071e3, #00bfff, #0071e3)',
                              backgroundSize: '200% 100%',
                              animation: 'rotate-gradient 3s linear infinite',
                              filter: 'blur(0.5px)'
                            }}
                          />
                          <Button variant="hero-blue-ring" size="hero" asChild>
                            <a href={applyUrl} target="_blank" rel="noopener noreferrer">
                              Apply
                            </a>
                          </Button>
                        </div>
                        <Button
                          variant="hero-outline" 
                          size="hero" 
                          className="border border-white/20 hover:border-[#0071e3]/60 hover:shadow-[0_0_24px_rgba(0,113,227,0.3)] transition-all duration-300 hover:scale-[1.02]"
                          onClick={() => document.getElementById('framework')?.scrollIntoView({ behavior: 'smooth' })}
                        >
                          More Details
                        </Button>
                      </div>
                    </SimpleReveal>
                  </div>

                  {/* Right Column - Video Phone (hidden on mobile) */}
                  <div className="hidden lg:block lg:absolute lg:right-12 lg:-top-4 lg:w-[320px] xl:w-[340px]">
                    <VideoPhoneMockup />
                  </div>
                  
                </div>
              </div>
            </section>

            {/* Video Introduction Section */}
            <WelcomeVideoSection applyUrl={applyUrl} />
          </DarkChapter>

          {/* The Real Issue Section - White background starts here */}
          <div id="welcome">
            <RealIssueSection />
          </div>

          {/* Artist Growth Formula - Where Art Meets Science */}
        <ArtMeetsScienceFormula />
        
        <IntroducingBar />
        <FounderMessageSection />

        {/* Real Results Section - Apple-style blue grid */}
        <section id="artist-results" data-nav-theme="dark" className="section-results py-12 lg:py-20 relative" style={{ background: 'radial-gradient(ellipse at center, hsl(0, 0%, 7%) 0%, hsl(0, 0%, 3%) 70%)' }}>
          {/* Base grid - emerging fade from top */}
          <div 
            className="absolute -top-96 inset-x-0 bottom-0 pointer-events-none"
            style={{
              backgroundImage: `
                linear-gradient(to right, rgba(0, 113, 227, 0.03) 1px, transparent 1px),
                linear-gradient(to bottom, rgba(0, 113, 227, 0.03) 1px, transparent 1px)
              `,
              backgroundSize: '60px 60px',
              backgroundPosition: 'center center',
              maskImage: 'linear-gradient(to bottom, transparent 0%, black 25%, black 100%)',
              WebkitMaskImage: 'linear-gradient(to bottom, transparent 0%, black 25%, black 100%)'
            }}
          />

          {/* Radial spotlight grid - brightens around content */}
          <div 
            className="absolute inset-0 pointer-events-none"
            style={{
              backgroundImage: `
                linear-gradient(to right, rgba(0, 113, 227, 0.06) 1px, transparent 1px),
                linear-gradient(to bottom, rgba(0, 113, 227, 0.06) 1px, transparent 1px)
              `,
              backgroundSize: '60px 60px',
              backgroundPosition: 'center center',
              maskImage: 'radial-gradient(ellipse 900px 700px at center 40%, black 0%, transparent 70%)',
              WebkitMaskImage: 'radial-gradient(ellipse 900px 700px at center 40%, black 0%, transparent 70%)'
            }}
          />
          
          <div className="max-w-7xl mx-auto px-6 relative z-10">
            {/* Badge */}
            <SimpleReveal>
              <div className="flex justify-center mb-6">
                <div className="inline-flex items-center gap-2 px-2 py-1 md:px-3 md:py-1.5 bg-[#0071e3]/10 border border-[#0071e3]/25 rounded-md shadow-[0_0_20px_rgba(0,113,227,0.3)] md:shadow-none transition-all duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] hover:bg-[#0071e3]/15 hover:border-[#0071e3]/35 hover:shadow-[0_0_20px_rgba(0,113,227,0.3)]">
                  <span className="font-mono text-xs font-medium text-[#0071e3] tracking-wider md:tracking-[0.15em] uppercase">
                    &gt; CASE STUDIES
                  </span>
                </div>
              </div>
            </SimpleReveal>

            {/* Title */}
            <SimpleReveal className="text-center">
              <h2 className="headline-lg text-white w-fit mx-auto mb-2">
                Real Growth.<br />Real Results. Real Fast.
              </h2>
            </SimpleReveal>
            
            {/* Subheading */}
            <SimpleReveal>
            <p className="body-large text-white/70 text-center max-w-2xl mx-auto mb-12">
              The playbook that compounds repeatable results.
            </p>
            </SimpleReveal>

            {/* Result-focused case study tabs */}
            <Suspense fallback={<div className="h-96 flex items-center justify-center"><div className="text-white/50">Loading...</div></div>}>
              <DetailedCaseStudyTabs />
            </Suspense>
          </div>
        </section>

        {/* Testimonials Section */}
        <Suspense fallback={<div className="h-96 bg-gray-50 flex items-center justify-center"><div className="text-gray-500">Loading...</div></div>}>
          <TestimonialsSection />
        </Suspense>

        {/* Before/After CTA Section */}
        <Suspense fallback={<div className="h-96 flex items-center justify-center"><div className="text-white/50">Loading...</div></div>}>
          <BeforeAfterCTA applyUrl={applyUrl} />
        </Suspense>

          {/* Footer */}
          <Suspense fallback={<div className="h-20 bg-gray-900" />}>
            <Footer applyUrl={applyUrl} />
          </Suspense>
      </div>
    </>
  );
}
